import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  FileText, PenTool, Banknote, Shield, RefreshCw, 
  Clock, CheckCircle, Users, AlertTriangle 
} from 'lucide-react';

export default function LandlordsTenancy() {
  const steps = [
    {
      title: 'Tenant Accepts Offer',
      description: 'Once you approve an application, the tenant accepts and pays the holding deposit.'
    },
    {
      title: 'Contract Generated',
      description: 'We generate a jurisdiction-specific tenancy agreement with all your terms.'
    },
    {
      title: 'E-Signatures',
      description: 'Both parties sign digitally - no printing, posting, or scanning required.'
    },
    {
      title: 'Deposit Protected',
      description: 'Tenant\'s deposit is collected and protected with a government scheme automatically.'
    },
    {
      title: 'Move-In Scheduled',
      description: 'Inventory inspection booked, keys arranged, tenant moves in on schedule.'
    },
    {
      title: 'Ongoing Management',
      description: 'Rent collection starts automatically. We handle everything from here.'
    }
  ];

  const benefits = [
    {
      icon: FileText,
      title: 'Digital Contracts',
      description: 'Legally compliant tenancy agreements for AST (England), PRT (Scotland), or SOC (Wales).'
    },
    {
      icon: PenTool,
      title: 'E-Signatures',
      description: 'Tenants and landlords sign digitally. Completed contracts stored securely.'
    },
    {
      icon: Shield,
      title: 'Deposit Protection',
      description: 'Deposits protected automatically with TDS, DPS, or mydeposits within legal timeframes.'
    },
    {
      icon: Banknote,
      title: 'Rent Collection',
      description: 'Direct debit setup, automatic reminders, arrears tracking - all handled.'
    },
    {
      icon: RefreshCw,
      title: 'Renewals',
      description: 'We contact tenants before the end of term, handle negotiations, generate new contracts.'
    },
    {
      icon: Clock,
      title: 'Notice Periods',
      description: 'We calculate correct notice periods for your jurisdiction and guide you through the process.'
    },
    {
      icon: Users,
      title: 'Multi-Tenant',
      description: 'Joint tenancies, room-by-room HMOs, guarantors - all supported.'
    },
    {
      icon: AlertTriangle,
      title: 'End of Tenancy',
      description: 'Check-out inspections, deposit negotiations, deductions handled fairly.'
    }
  ];

  const faqs = [
    {
      question: 'What type of tenancy agreement do you use?',
      answer: 'We use the appropriate agreement for your jurisdiction: Assured Shorthold Tenancy (England), Private Residential Tenancy (Scotland), or Standard Occupation Contract (Wales). All are legally compliant and regularly updated.'
    },
    {
      question: 'How is the deposit protected?',
      answer: 'We partner with government-authorized deposit schemes. The deposit is protected within the legal timeframe and the tenant receives the prescribed information automatically.'
    },
    {
      question: 'What happens at the end of a tenancy?',
      answer: 'We coordinate check-out inspections, compare against check-in inventory, and manage any deposit deduction discussions. If you both agree, funds are released quickly.'
    },
    {
      question: 'Can tenants pay rent by Direct Debit?',
      answer: 'Yes - we set up Direct Debit for all tenants as standard. This means reliable, automated rent collection every month.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="TENANCY & DEPOSITS"
        title="From Offer to Move-In,"
        subtitle="Seamlessly"
        description="Digital contracts, e-signatures, automatic deposit protection, and rent collection - the entire tenancy lifecycle, handled."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
        secondaryCTA={{ href: '/landlords', label: 'Back to Overview' }}
        gradient="from-purple-50 via-white to-slate-50"
      />

      <HowItWorks title="Creating a Tenancy" steps={steps} />

      <BenefitsGrid title="Tenancy Features" benefits={benefits} />

      {/* Deposit Schemes */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold mb-4">Deposit Protection Partners</h2>
          <p className="text-slate-600 mb-8">We work with government-authorized schemes to protect tenant deposits</p>
          <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
            <span className="text-lg font-semibold">TDS</span>
            <span className="text-lg font-semibold">DPS</span>
            <span className="text-lg font-semibold">mydeposits</span>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to streamline your tenancies?"
        description="Let LetWise handle the paperwork while you focus on your portfolio."
        primaryCTA={{ href: '/register', label: 'Create Account' }}
      />
    </PublicLayout>
  );
}
